import type { Role } from '@/modules/roles/composables/useRoles'
import { useRoleAbilitiesStore } from '@/stores/roleAbilities'
import { storeToRefs } from 'pinia'
import { ref } from 'vue'

// Guard de "guardado obligatorio" del editor de roles, compartido por la página
// standalone /roles (IndexPage) y el tab de roles de la empresa (RolesTab).
//
// Los toggles de permisos mutan el store `roleAbilities` directamente y cambiar
// de rol remonta el panel, por lo que salir con cambios sin guardar los pisaría
// en silencio. Este guard intercepta cualquier navegación (cambiar de rol,
// volver a la lista, cambiar de empresa) y OBLIGA a guardar antes de continuar.
//
// El panel expone `save()` que RECHAZA si el guardado falla; el guard solo
// continúa cuando el guardado tuvo éxito, y en fallo deja el modal abierto con
// una salida explícita "Descartar cambios y continuar" (nunca un descarte en
// silencio, que es justo lo que este flujo elimina).
export default function useRoleSwitchGuard() {
	const { hasUnsavedChanges } = storeToRefs(useRoleAbilitiesStore())

	const selectedRole = ref<Role | null>(null)
	const mobileView = ref<'list' | 'abilities'>('list')
	const abilitiesPanelRef = ref<{ save: () => Promise<void> } | null>(null)

	const showUnsavedModal = ref(false)
	const savingBeforeSwitch = ref(false)
	const saveFailed = ref(false)
	const pendingAction = ref<(() => void) | null>(null)

	const applyRoleSwitch = (role: Role | null) => {
		if (role) {
			selectedRole.value = role
			mobileView.value = 'abilities'
		} else {
			selectedRole.value = null
			mobileView.value = 'list'
		}
	}

	// Corre `action` de inmediato si no hay cambios sin guardar; si los hay, la
	// deja pendiente y abre el modal de guardado obligatorio.
	const runGuarded = (action: () => void) => {
		if (selectedRole.value && hasUnsavedChanges.value) {
			pendingAction.value = action
			saveFailed.value = false
			showUnsavedModal.value = true
			return
		}

		action()
	}

	const selectRole = (role: Role) => {
		const target = selectedRole.value?.id === role.id ? null : role
		runGuarded(() => applyRoleSwitch(target))
	}

	const backToList = () => runGuarded(() => applyRoleSwitch(null))

	const resolvePending = () => {
		showUnsavedModal.value = false
		saveFailed.value = false
		const action = pendingAction.value
		pendingAction.value = null
		action?.()
	}

	const saveAndContinue = async () => {
		savingBeforeSwitch.value = true
		saveFailed.value = false
		try {
			await abilitiesPanelRef.value?.save()
			resolvePending()
		} catch {
			// El panel ya mostró el toast de error; conservamos los toggles y el
			// modal abierto para reintentar o descartar de forma explícita.
			saveFailed.value = true
		} finally {
			savingBeforeSwitch.value = false
		}
	}

	const discardAndContinue = () => resolvePending()

	const keepEditing = () => {
		if (savingBeforeSwitch.value) return
		showUnsavedModal.value = false
		saveFailed.value = false
		pendingAction.value = null
	}

	return {
		selectedRole,
		mobileView,
		abilitiesPanelRef,
		hasUnsavedChanges,
		showUnsavedModal,
		savingBeforeSwitch,
		saveFailed,
		runGuarded,
		applyRoleSwitch,
		selectRole,
		backToList,
		saveAndContinue,
		discardAndContinue,
		keepEditing,
	}
}
